// The sign-in card with desyr ID and "Your circles" (36-desyr-id.md, Screens), in the sign-in frame.
import { useEffect, useLayoutEffect, useState } from 'react';
import { Avatar } from '../blocks/Bits';
import { Icon, icons } from '../blocks/icons';
import type { Config, DesyrIdConfig } from '../core/config';
import { t } from '../i18n';
import { ErrorLine, Frame, Heading, MainButton } from '../signin/parts';
import { onDevice, openAfterSignIn, retry, startCircles, stateOf, useCircles } from './circles';
import { PasskeyCard } from './Invitations';
import { SignInHere } from './DeviceLink';
import { finishSigningOut, IdError, signInAgain, startSignIn, unlessLeft } from './oidc';
import { readSession } from '../core/session';
import { dropAuto, readId, signedOutMark } from './store';
import { StartScreen } from '../start/screens';
import { startStep } from '../start/startLayer';
import { Skeletons } from '../blocks/Wait';

/**
 * "Continue with desyr ID" (36): the service's sign-in in this window; `failure` after one that went
 * wrong. `note`: "Your desyr ID is being deleted." over the card (39). `retired`: a set-up or recovery
 * link after the move (39, Behaviour 4), one heading and the button; signed in already, it opens desyr.
 */
/** Why the sign-in card shows an error line: desyr ID's own, or the account service's error page (36, rule 8). */
/** `idUnavailable`: the sessions call answered that desyr ID can't be reached (Behaviour 6.3). */
export type IdFailure = IdError['reason'] | 'signInError' | 'idUnavailable';

export function IdSignIn({ config, id, failure, note, retired, here: askHere, onLegacy }: { config: Config; id: DesyrIdConfig; failure?: IdFailure; note?: boolean; retired?: boolean; here?: boolean; onLegacy?: () => void }) {
  const [busy, setBusy] = useState(false);
  // "No passkey on this device?" shows "Sign in on this device" in the card's place (v1.58.46, with `deviceLink`).
  const [here, setHere] = useState(!!askHere && !!id.deviceLink);
  const [error, setError] = useState(failure === 'signInError' ? t('id.couldntSignIn') : failure === 'idUnavailable' ? t('id.unavailable') : failure === 'failed' || failure === 'unavailable' ? t('id.failed') : undefined);
  // Signed out here but desyr ID didn't confirm it (Behaviour 4.3.2, v1.58.63): the line and Finish signing out.
  const [unconfirmed] = useState(() => !retired && signedOutMark()?.ended === false);
  const [finishing, setFinishing] = useState(false);
  const go = () => {
    if (retired && readId() && readSession()) return location.assign('/home/');
    dropAuto(); // the person's own press, not a jump (2d)
    setBusy(true);
    setError(undefined);
    const failed = () => (setBusy(false), setError(t('id.failed')));
    startSignIn(id, retired ? '/login' : location.pathname + location.search).then(() => unlessLeft(failed), failed);
  };
  const finish = () => {
    setFinishing(true);
    setError(undefined);
    finishSigningOut(id).catch(() => (setFinishing(false), setError(t('id.unavailable'))));
  };
  if (here) return <SignInHere config={config} id={id} onBack={() => setHere(false)} />;
  return (
    <Frame config={config} waiting={busy} step="desyr-id">
      {note && (
        <p role="status" className="si-muted">
          {t('id.beingDeleted')}
        </p>
      )}
      <Heading sub={retired ? undefined : t('id.line')}>{t(retired ? 'id.retiredLink' : 'id.signIn')}</Heading>
      <form className="signin-form" onSubmit={(e) => (e.preventDefault(), go())}>
        <ErrorLine>{error}</ErrorLine>
        {unconfirmed && (
          <p className="si-quiet-line" role="status" data-desyr-sign-out-unconfirmed="line">
            {t('id.unconfirmed')}
          </p>
        )}
        <MainButton waiting={busy} disabled={finishing} autoFocus>
          {error ? t('id.tryAgain') : t('id.continue')}
        </MainButton>
        {unconfirmed && (
          <button type="button" className="btn btn-soft" disabled={busy || finishing} data-desyr-sign-out-unconfirmed="finish" onClick={finish}>
            {t('id.finishSignOut')}
          </button>
        )}
        {/* So that the jump to desyr ID's page isn't a surprise (v1.58.47). */}
        {!retired && <p className="si-quiet-line">{t('id.jumpLine')}</p>}
        {!retired && <p className="si-quiet-line">{t('id.noId')}</p>}
        {!retired && id.deviceLink && (
          <button type="button" className="si-link si-under" onClick={() => setHere(true)}>
            {t('id.noPasskeyHere')}
          </button>
        )}
        {!retired && <p className="si-quiet-line">{t('id.expiredLink')}</p>}
        {id.legacy && !retired && (
          <button type="button" className="si-link si-under" onClick={onLegacy}>
            {t('id.legacy')}
          </button>
        )}
      </form>
    </Frame>
  );
}

/**
 * Starting signed out (36, v1.58.51): from the start screen, without a click and without the card, desyr
 * ID's sign-in at once (as "Continue with desyr ID"), coming back to `page`; a failure shows the card.
 */
export function AutoSignIn({ id, page, onFailed }: { id: DesyrIdConfig; page: string; onFailed: () => void }) {
  useLayoutEffect(() => (startStep('auto'), () => startStep()), []);
  useEffect(() => {
    // Preparing it has 10 s (the discovery document and the pending entry; 2e, v1.58.63): past that, the card.
    const deadline = new AbortController();
    const fail = () => !deadline.signal.aborted && (deadline.abort(), onFailed());
    const timer = setTimeout(fail, 10_000);
    // Sent on its way, the window may still stay here (a refused address in WebKit): then the card too.
    startSignIn(id, page, undefined, deadline.signal).then(() => (clearTimeout(timer), unlessLeft(onFailed)), () => (clearTimeout(timer), fail()));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <StartScreen />;
}

/**
 * "Opening your circles…" (36, Screens, v1.58.25): the first circle's Home skeleton while the
 * sessions call runs, the line after 1.5 s; then desyr, "Your circles", or the sign-in card with its error.
 */
export function Opening({ config, onOpen, onCircles, onFailed }: { config: Config; onOpen: () => void; onCircles: () => void; onFailed: (why: IdFailure) => void }) {
  useEffect(() => {
    void openAfterSignIn(config).then((next) => (next === 'open' ? onOpen() : next === 'circles' ? onCircles() : onFailed(next)));
  }, []);
  // The start screen stands over it ("Opening your circles…" after 1.5 s; 03, 36, v1.58.48).
  return <StartScreen />;
}

/**
 * "Your circles" (36): each circle with its state; the hops of those that need one, one after the
 * other; "Open desyr" once one is ready, and by itself once all are (with nothing else to answer
 * here). `extra`: what later parts add (the invitations, the passkey card).
 */
export function YourCircles({ config, id, signInError, onOpen, onSignOut, extra }: { config: Config; id: DesyrIdConfig; signInError?: boolean; onOpen: () => void; onSignOut: () => void; extra?: React.ReactNode }) {
  const { answer, unreachable, logos } = useCircles();
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    void startCircles(config).finally(() => setLoaded(true));
  }, []);
  const circles = answer?.circles ?? [];
  const ready = circles.filter((c) => onDevice(c.mxid));
  const all = circles.length > 0 && ready.length === circles.length;
  // Also once the last invitation is joined here: the sessions call adds the circle without a reload (v1.58.15).
  const open = answer?.invitations?.length ?? 0;
  useEffect(() => {
    if (all && !open) onOpen();
  }, [all, open]);
  return (
    <Frame config={config} step="circles">
      <Heading sub={t('id.signedInAs', { email: readId()?.email ?? '' })}>{t('id.circles')}</Heading>
      <PasskeyCard id={id} />
      <ul className="id-circles" aria-label={t('id.circles')} aria-busy={!loaded || undefined}>
        {/* While the list loads: skeleton rows, a logo shape and a name bar (36, v1.58.25). */}
        {!loaded && !circles.length && <Skeletons as="li" count={2} className="" />}
        {circles.map((c) => {
          const state = stateOf(c);
          return (
            <li key={c.server} data-desyr-circle={c.server} data-desyr-circle-state={state}>
              <Avatar id={c.server} name={c.name} src={logos[c.slug]} size={40} round={false} letters={2} />
              <strong>{c.name}</strong>
              <span role="status">
                {state === 'ready' && <Icon svg={icons.check} size={16} />}
                {t(state === 'signing-in' ? 'id.signingIn' : state === 'ready' ? 'id.ready' : state === 'passkey' ? 'id.passkeyNeeded' : 'id.couldntSignIn')}
              </span>
              {state === 'failed' && (
                <button type="button" className="si-link" onClick={() => void retry(config, c)}>
                  {t('id.retry')}
                </button>
              )}
              {state === 'passkey' && (
                <button type="button" className="si-link" onClick={() => void signInAgain(id)}>
                  {t('id.signInAgain')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {loaded && !circles.length && !unreachable && <p className="si-muted">{t('id.noCircle')}</p>}
      {signInError && <ErrorLine>{t('id.couldntSignIn')}</ErrorLine>}
      {unreachable && <ErrorLine>{t('id.cantLoad')}</ErrorLine>}
      {unreachable && (
        <button type="button" className="si-link" onClick={() => void startCircles(config)}>
          {t('id.retry')}
        </button>
      )}
      {extra}
      {ready.length > 0 ? (
        <MainButton type="button" onClick={onOpen}>
          {t('id.open')}
        </MainButton>
      ) : (
        loaded && !circles.length && (
          <button type="button" className="si-link si-under" onClick={onSignOut}>
            {t('id.signOut')}
          </button>
        )
      )}
    </Frame>
  );
}
